import { reflect } from '@effector/reflect';
import { TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { PenSquare } from 'lucide-react';
import { equals, not } from 'patronum';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DEVICE_NAME_MIN_LENGTH, DEVICE_NAME_MAX_LENGTH } from 'entities/device';
import { authModel, USER_ROLES } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { createModalForm, ModalFormProps } from 'shared/ui';
import { EditDeviceDetailedModel } from '../../model';

export const createEditDeviceDetailedModal = (model: EditDeviceDetailedModel) => {
	const ModalForm = createModalForm(model);

	const NameField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'edit-device-detailed-name-input',
			label: translated('edit-device.name'),
			value: model.form.fields.name.$value,
			errorProps: { 'data-test-id': 'edit-device-detailed-name-input-error' },
			error: combine(
				[model.form.fields.name.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-device.name'),
						minLength: DEVICE_NAME_MIN_LENGTH,
						maxLength: DEVICE_NAME_MAX_LENGTH,
					}),
			),
			onChange: (e) => model.form.fields.name.change(e.target.value),
			required: true,
		},
	});

	const EditDeviceDetailedModal = forwardRef<HTMLDivElement, ModalFormProps>(
		({ ...props }, ref) => {
			const { t } = useTranslation();

			const [pending, pristine, closed] = useUnit([
				model.$pending,
				model.form.$pristine,
				model.modalClosed,
			]);

			return (
				<ModalForm
					ref={ref}
					title={t('edit-device.title')}
					cancelButtonProps={{
						children: t('edit-device.cancel'),
					}}
					submitButtonProps={{
						children: t('edit-device.save'),
						loading: pending,
						disabled: pristine,
					}}
					onExitTransitionEnd={closed}
					{...props}
				>
					<NameField />
				</ModalForm>
			);
		},
	);

	const EditDeviceDetailedModalActionIcon = reflect({
		view: ModalForm.ActionIcon,
		bind: {
			'data-test-id': 'edit-device-detailed-action-icon',
			variant: 'ghost-secondary',
			children: <PenSquare />,
			disabled: not(equals(authModel.$role, USER_ROLES.god)),
		},
	});

	return Object.assign(EditDeviceDetailedModal, {
		ActionIcon: EditDeviceDetailedModalActionIcon,
	});
};
